import { useEffect, useMemo, useState } from "react";
import {
    Modal, ModalBody, ModalFooter, ModalHeader,
    Form, FormGroup, Label, Input, Button, Spinner, Alert
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from 'react-router-dom';
import { createQuoteRevision, updateQuoteRevision } from "../data/quoteRevisionService";
import { useSelector } from "react-redux";
import { useQuery } from '@tanstack/react-query';
import { fetchQuoteRevisionsDropdown } from '../data/quoteRevisionService';
import { useTheme } from '../../../hooks/useTheme';
import Select from 'react-select';
import CustomMenuList from '../../../components/utility/CustomMenuList';
import { toast } from 'react-toastify';
import { createAudit } from "../../../features/audit/data/auditService";
import { findChangesInObject } from "../../../utils/objectUtils";
import { createNewRevisionFolderStructure } from "../../sharepoint/data/sharepointService";
import { fetchOpportunityById } from "../../opportunities/data/opportunitiesService";

/**
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - quoteId: number
 *  - revision?: existing revision (if provided, we edit)
 *  - onSaved?: () => void  (optional; otherwise we invalidate + close)
 *  - showIsMainToggle?: boolean (default false — keep "main" action on the star)
 */
const QuoteRevisionModal = ({ isOpen, onClose, quoteId, opportunityId, revision, onSaved, showIsMainToggle = false, isActive }) => {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const isEdit = Boolean(revision?.id);

    const user = useSelector((state) => state.authentication?.user);

    const getOpportunity = async () => {
        const opportunity = await fetchOpportunityById(opportunityId);
        return opportunity?.records;
    }

    // Fetch data using useQuery
    const {
        data: opportunity = {}
    } = useQuery({
        queryKey: ["opportunityData", opportunityId],
        queryFn: getOpportunity,
        enabled: opportunityId !== undefined,
        staleTime: 5 * 60_000, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const getRevisions = async () => {
        const revisions = await fetchQuoteRevisionsDropdown(quoteId);
        return revisions;
    }

    const {
        data: revisions
    } = useQuery({
        queryKey: ["quoteRevisionsDropdown", quoteId],
        queryFn: getRevisions,
        enabled: !!quoteId,
        staleTime: 60000
    });

    const mainRevision = useMemo(() => (revisions && revisions.find(f => f.isMain)) ?? {}, [revisions]);

    const defaults = useMemo(() => ({
        Name: revision?.name ?? "",
        Notes: revision?.notes ?? "",
        IsMain: isEdit ? !!revision?.isMain : true, // usually controlled via the star; keep hidden by default
        RevisionId: mainRevision?.id ?? 0
    }), [revision, mainRevision]);

    const [form, setForm] = useState(defaults);
    const [submitted, setSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (isOpen) {
            setForm(defaults);
            setSubmitted(false);
            setErrorMsg("");
        }
    }, [isOpen, defaults]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const { mutateAsync, isPending } = useMutation({
        mutationFn: async () => {
            const payload = {
                quoteId: Number(quoteId),
                revisionNumber: revision?.revisionNumber ?? null, //keep existing number if editing; null on create
                quoteTemplateId: isEdit ? revision?.quoteTemplateId ?? null : revisions.find(f => f.id === selectedRevision.value)?.quoteTemplateId ?? null,
                name: form.Name?.trim() ?? "",
                notes: form.Notes?.trim() ?? "",
                isMain: !!form.IsMain,
                revisionId: form.RevisionId,
                additionalInformation: revision?.additionalInformation ?? null,

                quoteAdditionalFields: isEdit ? revision?.quoteAdditionalFields ?? [] : JSON.parse(revisions.find(f => f.id === selectedRevision.value)?.quoteAdditionalFieldsJson) ?? [],
                quoteOrderInformation: isEdit ? revision?.quoteOrderInformation ?? {} : JSON.parse(revisions.find(f => f.id === selectedRevision.value)?.quoteOrderInformationJson) ?? {},
                quoteCustomisation: isEdit ? revision?.quoteCustomisation ?? {} : JSON.parse(revisions.find(f => f.id === selectedRevision.value)?.quoteCustomisationJson) ?? {},

                ...(isEdit ? {
                    updatedBy: user?.sub || "unknown",
                    createdBy: revision?.createdBy
                } : null),
                ...(!isEdit ? { createdBy: user?.sub || "unknown" } : null), // only on create
                ...(isEdit ? { id: revision.id } : null), // only on edit
            };

            const result = isEdit ? await updateQuoteRevision(payload) : await createQuoteRevision(payload);
            if (!isEdit) {
                const revisionNumber = revisions.find(f => f.id === form.RevisionId).revisionNumber;
                const { message, responseOk } = await createNewRevisionFolderStructure(opportunity.SharePoint_Folder__c, payload.quoteId, result.data.revisionNumber, isActive, payload.isMain, revisionNumber);
                if (!responseOk) {
                    toast.error(message);
                    console.error(`Quote ${payload.quoteId} revision ${revisionNumber} - ${message}`);
                    createAudit({ "action": "Create revision in sharepoint", "result": "Error", "source": `quoteRevision.${form.RevisionId}`, "payload": JSON.stringify({ ...payload, message }) });
                }
                else {
                    createAudit({ "action": "Create revision in sharepoint", "result": "Successful", "source": `quoteRevision.${form.RevisionId}`, "payload": JSON.stringify(payload) });
                }
            }
            auditChanges(isEdit, payload);

            return result;
        },
        onSuccess: async () => {
            // Refresh the revisions list
            await queryClient.invalidateQueries({ queryKey: ["quoteRevisions", quoteId] });
            await queryClient.invalidateQueries({ queryKey: ["quoteRevisionsDropdown", quoteId] });
            onSaved?.();
            onClose();
        },
        onError: (err) => setErrorMsg(err?.message || (isEdit ? "Failed to update revision." : "Failed to create revision.")),
    });

    const handleSave = async (e) => {
        e.preventDefault();
        if (!validate(form)) return false;
        setSubmitted(true);
        setErrorMsg("");
        // No required fields in this small modal, but keep the pattern if you add them later

        const result = await mutateAsync();
        if (!isEdit) {
            const newRevisionId = result.data.id;
            navigate(`/opportunities/${opportunityId}/${quoteId}/${newRevisionId}`);
        }
    };

    const validate = () => {
        let blankFields = [];
        if (!form.Name) blankFields.push("Name");
        if (!form.Notes) blankFields.push("Notes");
        if (blankFields.length > 0) {
            toast.warning(`Please fill in the following fields: ${blankFields.join(", ")}`);
            return false
        }
        return true
    }

    const revisionOptions = useMemo(
        () => (revisions ?? []).map(r => ({ label: r.name, value: r.id })),
        [revisions]
    );

    const selectedRevision = useMemo(
        () => revisionOptions.find(o => o.value === form.RevisionId) ?? null,
        [revisionOptions, form.RevisionId]
    );

    const selectStyle = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        })
    };

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const auditChanges = (isEdit, payload) => {
        let diffs = {};

        if (isEdit) {
            const minimalRevision = revision ? { name: revision.name, notes: revision.notes, isMain: revision.isMain } : {};
            const minimalPayload = { name: payload.name, notes: payload.notes, isMain: payload.isMain };

            diffs = findChangesInObject(minimalRevision, minimalPayload, false, true);
            if (Object.keys(diffs).length === 0) return;
        } else {
            delete payload.quoteId
            delete payload.revisionNumber
            delete payload.quoteTemplateId
            delete payload.revisionId
            delete payload.quoteAdditionalFields
            delete payload.quoteOrderInformation
            delete payload.quoteCustomisation
            delete payload.createdBy
        }

        const action = isEdit ? "Revision updated" : "Revision created";
        const content = isEdit ? JSON.stringify({ revisionNumber: revision?.revisionNumber ?? null, ...diffs }) : JSON.stringify(payload);
        createAudit({ "action": action, "result": "Successful", "source": `quote.${quoteId}`, "payload": content })
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered size="lg" backdrop="static">
            <ModalHeader toggle={onClose}>{isEdit ? "Edit Revision" : "Create Revision"}</ModalHeader>
            <ModalBody>
                {errorMsg && <Alert color="danger" className="mb-3">{errorMsg}</Alert>}

                <Form onSubmit={handleSave}>
                    <FormGroup>
                        <Label for="Name">Name*</Label>
                        <Input
                            id="Name"
                            name="Name"
                            type="text"
                            value={form.Name}
                            onChange={onChange}
                            placeholder="Add Name"
                            required
                            invalid={!form.Name}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="Notes">Notes*</Label>
                        <Input
                            id="Notes"
                            name="Notes"
                            type="textarea"
                            value={form.Notes}
                            onChange={onChange}
                            placeholder="Add notes"
                            rows={3}
                            required
                            invalid={!form.Notes}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="revisionId" className="w-100">Revision*</Label>
                        <Select
                            inputId="revisionId"
                            name="revisionId"
                            value={selectedRevision}
                            options={revisionOptions}
                            onChange={(opt) => {
                                setForm({ ...form, RevisionId: opt?.value ?? undefined, opportunityName: opt?.label ?? undefined });
                            }}
                            placeholder="Select an option..."
                            theme={customTheme}
                            styles={{
                                ...selectStyle,
                                menuPortal: base => ({ ...base, zIndex: 9999 }),
                                menu: base => ({ ...base, zIndex: 9999 })
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            components={{ MenuList: CustomMenuList }}
                            className="w-100"
                            isDisabled={isEdit}
                        />
                    </FormGroup>

                    <FormGroup check className="mt-2">
                        <Input
                            type="checkbox"
                            id="IsMain"
                            name="IsMain"
                            checked={!!form.IsMain}
                            onChange={onChange}
                        />
                        <Label for="IsMain" check>Set as main revision</Label>
                    </FormGroup>

                </Form>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" outline onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button color="primary" onClick={handleSave} disabled={isPending}>
                    {isPending ? (<><Spinner size="sm" className="me-2" />Saving…</>) : (isEdit ? "Save changes" : "Create")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default QuoteRevisionModal;
